import { BusinessReviewButtonToVisible } from "@/context/data.types";
import Constants from "@/static/constants/constants.json";
import transitions from "@/constants/transitions";

const auditTransitions = transitions.audits_and_visits;

type Visibility = Record<string, boolean>;
type Rule = { label: string; visibility: Visibility };
type TransitionRules = {
  CREATE_PRODUCT: Rule[];
  VENDOR_ASSESSMENT: Rule[];
  VENDOR_ASSESSMENT_FORM_TABS: Rule[];
  TASTING_PANEL: Rule[];
  PRE_PANEL_INFORMATION: Rule[];
  BUSINESS_QUESTIONAIRE: Rule[];
  VENDOR_FORM: Rule[];
  AP_COMPLIANCE_DOCUMENTS: Rule[];
  AP_MAIN_DETAILS: Rule[];
  AP_PACKAGING: Rule[];
  AP_RECIPE: Rule[];
  AP_RAW_MATERIALS: Rule[];
  AP_ALLERGENS: Rule[];
  AP_NUTRITION: Rule[];
  AP_STORAGE: Rule[];
  SITE_AUDIT_DETAILS: Rule[];
};

// Centralized transition rules - organized by component/form
export const TRANSITION_RULES: TransitionRules = {
  CREATE_PRODUCT: [
    {
      label: "hold",
      visibility: { showHold: true },
    },
    {
      label: "archive",
      visibility: { showCancel: true },
    },
    {
      label: "vpa_share_for_review",
      visibility: { showSFR: true },
    },
    {
      label: "vpa_review_complete",
      visibility: { showRC: true },
    },
    {
      label: "vpa_review_completed",
      visibility: { showRCed: true },
    },
    {
      label: "vpa_request_revision",
      visibility: { showRFR: true },
    },
    {
      label: "reactivate_idea",
      visibility: { showReactivate: true },
    },
    {
      label: "delete_idea",
      visibility: { showDelete: true },
    },
    {
      label: "initiate_vendor_tasks",
      visibility: { showPVA: true },
    },
  ],
  VENDOR_ASSESSMENT: [
    {
      label: "share_for_review",
      visibility: { showShareForReview: true },
    },
    {
      label: "review_complete",
      visibility: { showReviewComplete: true },
    },
    {
      label: "review_completed",
      visibility: { showReviewCompleted: true },
    },
    {
      label: "request_revision",
      visibility: { showRequestForRevision: true },
    },
    {
      label: "reactivate_vendor",
      visibility: {
        showDelete: true,
        showReactivate: true,
        showPcSave: false,
      },
    },
    {
      label: "requested_revision",
      visibility: { showRequestedForRevision: true },
    },
    {
      label: "archive",
      visibility: { showCancel: true },
    },
    {
      label: "request_nfp_document",
      visibility: {
        showPcSave: false,
      },
    },
  ],
  VENDOR_ASSESSMENT_FORM_TABS: [
    {
      label: "vpa_cancel",
      visibility: { showCancel: true },
    },
    {
      label: "vpa_share_for_review",
      visibility: { showSFR: true },
    },
    {
      label: "vpa_review_complete",
      visibility: { showRC: true },
    },
    {
      label: "vpa_review_completed",
      visibility: { showRCed: true },
    },
    {
      label: "vpa_request_revision",
      visibility: { showRFR: true },
    },
  ],
  TASTING_PANEL: [
    {
      label: "share_for_tasting_panel",
      visibility: { showSFTPR: true },
    },
    {
      label: "share_for_tasting_panel_review",
      visibility: { showSFTPRAfterRevision: true },
    },
    {
      label: "share_for_testing_panel_review",
      visibility: { showSFTPRAfterRevision: true },
    },
    {
      label: "archive",
      visibility: { showArchive: true },
    },
    {
      label: "request_for_revision_for_data_points",
      visibility: { showRFR: true },
    },
    {
      label: "approve",
      visibility: { showApprove: true },
    },
    {
      label: "reactivate_vendor",
      visibility: { showSave: false, showReactivate: true },
    },
    {
      label: "request_nfp_document",
      visibility: {
        showRequestForNfpDocument: true,
        showPcSave: false,
      },
    },
  ],
  PRE_PANEL_INFORMATION: [
    {
      label: "review_complete",
      visibility: { showRC: true, showSave: true, showNext: false },
    },
  ],
  BUSINESS_QUESTIONAIRE: [
    {
      label: "hold",
      visibility: { showHold: true },
    },
    {
      label: "send_for_re_panel",
      visibility: { showSFRP: true },
    },
    {
      label: "share_for_review",
      visibility: { showSFR: true },
    },
    {
      label: "shared_for_review",
      visibility: { showSFRed: true },
    },
    {
      label: "share_for_business_review",
      visibility: { showSFBR: true },
    },
    {
      label: "shared_for_business_review",
      visibility: { showSFBRed: true },
    },
    {
      label: "archive",
      visibility: { showArchive: true },
    },
    {
      label: "reactivate_vendor",
      visibility: { showSave: true, showReactivate: true },
    },
    {
      label: "more_info_needed",
      visibility: { showMIN: true },
    },
    {
      label: "ready_for_tasting_panel",
      visibility: { showRFTP: true },
    },
  ],
  VENDOR_FORM: [
    {
      label: "share_for_review",
      visibility: { showShareForReview: true  },
    },
    {
      label: "submit_nfp_document",
      visibility: { showSubmitNfpDocument: true, showVendorSave: false },
    },
    {
      label: "shared_for_review",
      visibility: { showSharedForReview: true },
    },
  ],
  AP_COMPLIANCE_DOCUMENTS: [
    {
      label: "share_for_review",
      visibility: { showShareForReview: true },
    },
    {
      label: "compliance_save",
      visibility: { showSave: true },
    },
    {
      label: "request_for_revision",
      visibility: { showRequestForRevision: true, showNext: false },
    },
    {
      label: "review_complete",
      visibility: { showReviewComplete: true, showSave: false },
    },
  ],
  AP_MAIN_DETAILS: [
    {
      label: "main_details_save",
      visibility: { showSave: true },
    },
    {
      label: "share_for_review",
      visibility: { showShareForReview: true },
    },
    {
      label: "request_for_revision",
      visibility: { showRequestForRevision: true, showNext: false },
    },
    {
      label: "review_complete",
      visibility: { showReviewComplete: true, showSave: false },
    },
  ],
  AP_PACKAGING: [
    {
      label: "share_for_review",
      visibility: { showShareForReview: true },
    },
    {
      label: "packaging_save",
      visibility: { showSave: true },
    },
    {
      label: "request_for_revision",
      visibility: { showRequestForRevision: true },
    },
    {
      label: "review_complete",
      visibility: { showRC: true },
    },
  ],
  AP_RECIPE: [
    {
      label: "recipe_save",
      visibility: { showSave: true },
    },
    {
      label: "request_for_revision",
      visibility: { showRequestForRevision: true },
    },
    {
      label: "review_complete",
      visibility: { showRC: true },
    },
  ],
  AP_RAW_MATERIALS: [
    {
      label: "raw_materials_save",
      visibility: { showSave: true },
    },
    {
      label: "request_for_revision",
      visibility: { showRequestForRevision: true },
    },
    {
      label: "review_complete",
      visibility: { showRC: true },
    },
  ],
  AP_ALLERGENS: [
    {
      label: "allergens_save",
      visibility: { showSave: true },
    },
  ],
  AP_NUTRITION: [
    {
      label: "request_for_revision",
      visibility: { showRequestForRevision: true },
    },
    {
      label: "nutrition_save",
      visibility: { showSave: true },
    },
    {
      label: "review_complete",
      visibility: { showRC: true, showSave: true },
    },
  ],
  AP_STORAGE: [
    {
      label: "storage_save",
      visibility: { showSave: true },
    },
    {
      label: "request_for_revision",
      visibility: { showRequestForRevision: true },
    },
    {
      label: "review_complete",
      visibility: { showRC: true },
    },
  ],
  SITE_AUDIT_DETAILS: [
    {
      label: auditTransitions.approve_audit,
      visibility: { showApprove: true },
    },
    {
      label: auditTransitions.unlock_audit,
      visibility: { showUnlockAudit: true },
    },
    {
      label: auditTransitions.cancel_audit,
      visibility: { showCancelAudit: true },
    },
    {
      label: auditTransitions.request_for_revision,
      visibility: { showRequestForRevision: true },
    },
    {
      label: auditTransitions.approve_extension_request,
      visibility: { showApproveExtend: true },
    },
    {
      label: auditTransitions.approve_letter_of_intent_request,
      visibility: { showApproveExtend: true },
    },
    {
      label: auditTransitions.deny_extension_request,
      visibility: { showDenyExtensionRequest: true },
    },
    {
      label: auditTransitions.deny_letter_of_intent_request,
      visibility: { showDenyExtensionRequest: true },
    },
    {
      label: auditTransitions.recomplete,
      visibility: { showRecomplete: true },
    },
    {
      label: auditTransitions.submit_for_review,
      visibility: { showSubmitForReview: true },
    },
  ],
};

export const CUSTOM_RULES: any = {
  CREATE_PRODUCT: (status: string, visibility: any, showVendorInfoForm: boolean) => {
    if (
      status?.toLowerCase() === Constants.ARCHIVED_STATUS?.toLowerCase() ||
      status?.toLowerCase() === Constants.ON_HOLD_STATUS?.toLowerCase()
    ) {
      visibility.showSave = false;
      visibility.showPVA = false;
    } else {
      visibility.showSave = true;
      if (showVendorInfoForm) {
        visibility.showPVA = true;
      }
    }
    return visibility;
  },
  VENDOR_ASSESSMENT: (status: string, visibility: any) => {
    if (status?.toLowerCase() === Constants?.ARCHIVED_STATUS.replaceAll(" ", "_").toLowerCase()) {
      visibility.showPcSave = false;
    }
    return visibility;
  },
  VENDOR_ASSESSMENT_FORM_TABS: (status: string, visibility: any, workflowStatus: string) => {
    if (
      status?.toLowerCase() === Constants.NOT_STARTED_STATUS.toLowerCase() ||
      status?.toLowerCase() === Constants.IN_PROGRESS_STATUS.toLowerCase()
    ) {
      visibility.showSave = true;
      visibility.showPVA = true;
    }
    // else if (statusesOfProduct?.workflowStatus?.toLowerCase() === "on hold") {
    //   visibility.showCancel = true;
    //   visibility.showReactivate = true;
    // } // earlier this condition was commented
    else if (workflowStatus?.toLowerCase() === Constants.ARCHIVED_STATUS.toLowerCase()) {
      visibility.showDelete = true;
      // visibility.showReactivate = true;
    }
  },
  BUSINESS_QUESTIONAIRE: (status: string, visibility: BusinessReviewButtonToVisible, workflowTransitions?: any[]) => {
    if (!status) return visibility;
    const normalizedStatus = status.toLowerCase().replaceAll(" ", "_");
    
    // Check transitions for DELETE button visibility
    const transitionLabels = (workflowTransitions || []).map((t: any) => t.label?.toLowerCase() || "");
    const hasReactivateVendor = transitionLabels.includes("reactivate_vendor");
    const hasReactivateIdea = transitionLabels.includes("reactivate_idea");
    
    // DELETE button logic:
    // - Show DELETE when reactivate_vendor transition exists (vendor archived, idea not archived)
    // - Hide DELETE when reactivate_idea transition exists (idea is also archived)
    // - Hide DELETE when neither transition exists
    if (hasReactivateVendor && !hasReactivateIdea) {
      visibility.showDelete = true;
    } else {
      visibility.showDelete = false;
    }
    
    if (normalizedStatus === Constants?.ARCHIVED_STATUS.toLowerCase().replaceAll(" ", "_")) {
      visibility.showSave = false;
    }
    if (normalizedStatus === Constants?.HOLD_STATUS.toLowerCase().replaceAll(" ", "_")) {
      visibility.showSave = false;
    }
    return visibility;
  },
  TASTING_PANEL: (status: string, visibility: any, workflowTransitions?: any[]) => {
    if (!status) return visibility;
    const normalizedStatus = status.toLowerCase().replaceAll(" ", "_");
    
    // Check transitions for DELETE button visibility
    const transitionLabels = (workflowTransitions || []).map((t: any) => t.label?.toLowerCase() || "");
    const hasReactivateVendor = transitionLabels.includes("reactivate_vendor");
    const hasReactivateIdea = transitionLabels.includes("reactivate_idea");
    
    // DELETE button logic:
    // - Show DELETE when reactivate_vendor transition exists (vendor archived, idea not archived)
    // - Hide DELETE when reactivate_idea transition exists (idea is also archived)
    // - Hide DELETE when neither transition exists
    if (hasReactivateVendor && !hasReactivateIdea) {
      visibility.showDelete = true;
    } else {
      visibility.showDelete = false;
    }
    
    if (
      normalizedStatus === Constants?.ARCHIVED_STATUS.toLowerCase().replaceAll(" ", "_") ||
      normalizedStatus === Constants?.HOLD_STATUS.toLowerCase().replaceAll(" ", "_")
    ) {
      visibility.showSave = false;
    }
    return visibility;
  },
};
